<script setup>
import { computed, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useDetectDevice } from '@/composables/device'

import ServiceSelector from '@/components/shared/ServiceSelector.vue'
import ServiceListItem from '@/components/shared/ServiceListItem.vue'

const props = defineProps({
  // Array of selected components as { id, quantity } objects
  modelValue: {
    type: Array,
    default: () => [],
  },
  // Filtered services shown in the dropdown
  options: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  withQuantity: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'filter'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()

const selectedService = ref(null)

// Validation lives on the add-selector but checks the whole selection, so an empty
// list surfaces the standard Quasar error (icon + message) on the field itself.
const selectorRules = computed(() =>
  props.required
    ? [() => (props.modelValue?.length > 0) || i18n.t('common.required')]
    : undefined
)

const normalizeComponent = (component) => {
  if (component == null) return null
  if (typeof component === 'number') return { id: component, quantity: 1 }
  if (component.id == null) return null
  const quantity = component.quantity !== '' && component.quantity != null ? Number(component.quantity) : 1
  return { id: component.id, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 }
}
const normalizeComponents = (components) => (components || []).map(normalizeComponent).filter(Boolean)

// Resolve selected components into full service objects for the rich list rendering.
const currentServices = computed(() =>
  normalizeComponents(props.modelValue)
    .map(c => {
      const service = planningStore.getServices().find(s => s.id === c.id)
      return service ? { ...service, component_quantity: c.quantity } : null
    })
    .filter(Boolean)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
)

const onServiceAdd = (serviceId) => {
  if (!serviceId) return
  const current = normalizeComponents(props.modelValue)
  if (!current.some(c => c.id === serviceId)) {
    emit('update:modelValue', [...current, { id: serviceId, quantity: 1 }])
  }
  selectedService.value = null
}

const onServiceDelete = (serviceId) => {
  emit('update:modelValue', normalizeComponents(props.modelValue).filter(c => c.id !== serviceId))
}

const updateQuantity = (serviceId, value) => {
  const quantity = value !== '' && value != null ? Number(value) : 1
  emit('update:modelValue', normalizeComponents(props.modelValue).map(c =>
    c.id === serviceId
      ? { ...c, quantity: Number.isFinite(quantity) && quantity >= 1 ? quantity : 1 }
      : c
  ))
}
const incrementQuantity = (serviceId) => {
  const current = normalizeComponents(props.modelValue).find(c => c.id === serviceId)
  updateQuantity(serviceId, (current?.quantity || 1) + 1)
}
const decrementQuantity = (serviceId) => {
  const current = normalizeComponents(props.modelValue).find(c => c.id === serviceId)
  updateQuantity(serviceId, Math.max(1, (current?.quantity || 1) - 1))
}

const onFilter = (...args) => emit('filter', ...args)
</script>

<template>
  <div>
    <q-list
      v-if="currentServices.length"
      bordered
      separator
      class="q-mt-sm rounded-borders"
    >
      <div
        v-for="(service, index) in currentServices"
        :key="service.id"
        class="q-px-sm q-py-sm"
      >
        <div class="row items-center q-col-gutter-sm">
          <div :class="isMobile ? 'col-12' : 'col'">
            <service-list-item
              :service="service"
              :icon="readonly ? undefined : 'mdi-delete-outline'"
              icon-color="negative"
              dense
              @icon-click="onServiceDelete(service.id)"
            />
          </div>
          <div v-if="withQuantity && service.type !== portalStore.serviceTypeEnum.STAFF && !service.is_serialized" :class="isMobile ? 'col-12' : 'col-3'">
            <div class="row items-center no-wrap q-gutter-xs">
              <q-btn
                icon="mdi-minus"
                round
                dense
                flat
                color="primary"
                @click="decrementQuantity(service.id)"
              />
              <q-input
                :model-value="service.component_quantity"
                class="col"
                type="number"
                min="1"
                step="1"
                dense
                outlined
                :label="i18n.t('common.quantityAlt')"
                @update:model-value="updateQuantity(service.id, $event)"
              />
              <q-btn
                icon="mdi-plus"
                round
                dense
                flat
                color="primary"
                @click="incrementQuantity(service.id)"
              />
            </div>
          </div>
        </div>
        <q-separator v-if="index < currentServices.length - 1" class="q-mt-sm" />
      </div>
    </q-list>

    <service-selector
      v-if="!readonly"
      :model-value="selectedService"
      class="q-mt-sm"
      :label="label || i18n.t('common.service', 2)"
      :options="options"
      :rules="selectorRules"
      use-input
      :input-debounce="webSiteStore.getInputDebounce()"
      @filter="onFilter"
      @update:model-value="onServiceAdd"
    />
  </div>
</template>
